import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Package, Truck } from "lucide-react";
import { api } from "@/lib/api";
import { MediaThumb } from "@/components/MediaCard";
import { DEMO_SLUG } from "@/components/landing/HeroSections";

const DIGITAL = ["Unikatna poročna QR koda", "Neomejeno število gostov", "Nalaganje fotografij in videov", "Skupna poročna galerija", "Časovnica poroke", "Glasovna knjiga gostov", "Live Wall za lasten projektor ali TV", "365 dni hrambe", "Takojšnji prenosi in ZIP", "Digitalna knjiga gostov in srčki"];

export function ExampleGallery() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/public/w/${DEMO_SLUG}/media`).then((r) => setItems(r.data.items.slice(0, 9))).catch(() => {}); }, []);
  return (
    <section className="py-20 sm:py-28 bg-[#231F20] text-white" data-testid="example-gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow">Primer galerije</p>
            <h2 className="font-display text-4xl sm:text-5xl font-light mt-3">Tako so videti spomini gostov</h2>
            <p className="text-white/50 text-sm mt-3">Vzorčna vsebina demo poroke Jan & Mateja.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to={`/poroka/${DEMO_SLUG}`} className="btn-gold px-6 py-3 text-sm" data-testid="example-gallery-open-demo">Odpri celotno galerijo</Link>
            <button onClick={() => api.get("/public/demo-wall").then((r) => window.open(`/zaslon/${r.data.token}`, "_blank"))} className="px-6 py-3 text-sm rounded-full border border-white/30 hover:bg-white/10" data-testid="example-open-livewall">Preizkusi Live Wall</button>
          </div>
        </div>
        <div className="columns-2 md:columns-3 gap-4">
          {items.map((m) => <div key={m.id} className="masonry-item mb-4"><MediaThumb item={m} onOpen={() => (window.location.href = `/poroka/${DEMO_SLUG}`)} /></div>)}
        </div>
      </div>
    </section>
  );
}

export function Pricing() {
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => { api.get("/public/packages").then((r) => setPkgs(r.data)); }, []);
  return (
    <section id="cenik" className="py-20 sm:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 scroll-mt-16" data-testid="pricing-section">
      <p className="eyebrow">Cenik</p>
      <h2 className="font-display text-4xl sm:text-5xl font-light mt-3 max-w-2xl">Ista digitalna izkušnja v vsakem paketu</h2>
      <p className="text-[#6C6560] mt-4 max-w-2xl">Paketi se razlikujejo le po fizičnih QR izdelkih. Vsak vključuje 365 dni hrambe in jasno določen prostor za shranjevanje.</p>
      <div className="grid md:grid-cols-3 gap-6 mt-14 items-stretch">
        {pkgs.map((p, i) => (
          <div key={p.id} className={`card-lux p-7 flex flex-col ${i === pkgs.length - 1 ? "ring-1 ring-[#C5A880]" : ""}`} data-testid={`pricing-card-${p.id}`}>
            <p className="eyebrow">{i === pkgs.length - 1 ? "Najboljša izbira" : p.physical ? "Digitalno + QR kartice" : "Samo digitalno"}</p>
            <h3 className="font-display text-3xl mt-3">{p.name}</h3>
            <p className="mt-4"><span className="font-display text-5xl" data-testid={`pricing-price-${p.id}`}>{p.price} €</span>{p.per_table_price > 0 && <span className="text-lg text-[#6C6560]" data-testid={`pricing-pertable-${p.id}`}> + {p.per_table_price} € / miza</span>}</p>
            <p className="text-sm text-[#6C6560] mt-4 leading-relaxed">{p.description}</p>
            <div className="gold-rule my-6" />
            <ul className="space-y-2.5 text-sm flex-1">
              {(p.physical ? [`Vse iz paketa ${pkgs[0]?.name || "Osnovni"}`, ...p.items] : DIGITAL).map((t) => (
                <li key={t} className="flex gap-2"><Check size={16} className="text-[#B39264] shrink-0 mt-0.5" />{t}</li>
              ))}
              <li className="flex gap-2 text-[#6C6560]"><Check size={16} className="text-[#B39264] shrink-0 mt-0.5" />{p.storage_gb} GB prostora (možna nadgradnja)</li>
            </ul>
            <Link to={`/ustvari?paket=${p.id}`} className={`${i === pkgs.length - 1 ? "btn-dark" : "btn-line"} mt-8 py-3 text-center text-sm`} data-testid={`pricing-select-${p.id}`}>Izberi {p.name}</Link>
          </div>
        ))}
      </div>
    </section>
  );
}

const PRODUCT_IMG = "/brand/cards.webp";
const WELCOME_IMG = "/brand/welcome.webp";

export function Products() {
  return (
    <section id="izdelki" className="py-20 sm:py-28 bg-[#F3ECE1]/50 scroll-mt-16" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="eyebrow">Fizični QR izdelki</p>
        <h2 className="font-display text-4xl sm:text-5xl font-light mt-3 max-w-2xl">Elegantni QR izdelki, usklajeni z vajino poroko</h2>
        <div className="grid md:grid-cols-2 gap-6 mt-14">
          {[[PRODUCT_IMG, "Namizne kartice z QR kodo", "Elegantne zložene kartice z QR kodo in povabilom k deljenju — ena za vsako mizo, 3 € na mizo."], [WELCOME_IMG, "Velika tabla dobrodošlice", "Velika personalizirana tabla s stojalom, visoka približno 1 meter, za vhod ali prostor obreda. Vključena v paket Premium."]].map(([img, t, d]) => (
            <div key={t} className="card-lux overflow-hidden" data-testid="product-card">
              <img src={img} alt={t} className="w-full h-96 object-cover" />
              <div className="p-7 space-y-3"><h3 className="font-display text-2xl">{t}</h3><p className="text-sm text-[#6C6560]">{d}</p></div>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-6 text-sm">
          <div className="card-lux p-5 flex gap-3"><Package className="text-[#B39264] shrink-0" size={20} /><p><b>Teme:</b> minimalistična, romantična, rustikalna, moderna črno-bela, šampanjec, cvetlična, vintage.</p></div>
          <div className="card-lux p-5 flex gap-3"><Truck className="text-[#B39264] shrink-0" size={20} /><p><b>Izdelava in dostava:</b> priprava dizajna 2–3 dni, izdelava 7–10 dni, dostava 5–10 dni. Naročite vsaj 3 tedne pred poroko.</p></div>
          <div className="card-lux p-5 flex gap-3 sm:col-span-2" data-testid="products-on-request"><Package className="text-[#B39264] shrink-0" size={20} /><p><b>Po povpraševanju:</b> lesene ali akrilne tablice z QR kodo izdelamo po dogovoru. Pišite nam prek <a href="#kontakt" className="underline">kontaktnega obrazca</a>.</p></div>
        </div>
      </div>
    </section>
  );
}

const SERVICES = [["Live Wall", "0 €", "Projekcija fotografij v živo na vašem projektorju ali TV-ju — vključeno v vsak paket."], ["Foto kolaž", "39 €", "Ročno oblikovan personaliziran kolaž najlepših trenutkov."], ["Dodatna shramba", "od 19 €", "Dodaten prostor za shranjevanje po dogovoru ali podaljšanje hrambe galerije za 365 dni (19 €)."]];

export function Services() {
  return (
    <section className="py-20 sm:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="services-section">
      <p className="eyebrow">Dodatne storitve</p>
      <h2 className="font-display text-4xl sm:text-5xl font-light mt-3">Za še lepše spomine</h2>
      <p className="text-[#6C6560] mt-4 max-w-2xl">Storitve lahko naročita v svoji nadzorni plošči. Dodaten prostor za shranjevanje je na voljo po dogovoru.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {SERVICES.map(([t, p, d]) => (
          <div key={t} className="card-lux p-6 space-y-3" data-testid="service-card">
            <p className="font-display text-3xl text-[#B39264]">{p}</p>
            <h3 className="font-medium">{t}</h3>
            <p className="text-sm text-[#6C6560]">{d}</p>
          </div>
        ))}
      </div>
      <Rentals />
    </section>
  );
}

function Rentals() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/public/rentals").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div className="mt-16" data-testid="rentals-section">
      <p className="eyebrow">Najem projektorja in platna</p>
      <h3 className="font-display text-3xl sm:text-4xl font-light mt-3">Live Wall brez lastne opreme</h3>
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        {d.packages.map((p) => (
          <div key={p.id} className="card-lux p-6 space-y-3" data-testid={`rental-card-${p.id}`}>
            <p className="font-display text-3xl text-[#B39264]">+{p.price} €</p>
            <h4 className="font-medium">{p.name}</h4>
            <ul className="text-sm text-[#6C6560] space-y-1">{p.includes.map((i) => <li key={i} className="flex gap-2"><Check size={14} className="text-[#B39264] mt-0.5 shrink-0" />{i}</li>)}</ul>
            <p className="text-xs text-[#968E87]">Vračljiva varščina {p.deposit} €</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-[#6C6560] mt-4 max-w-3xl">{d.config.delivery_note} Za projekcijo podnevi priporočamo dovolj svetel projektor in ustrezno platno. Rezervacija je mogoča v nadzorni plošči poroke. <Link to="/pravno/najem" className="underline">Pogoji najema</Link></p>
    </div>
  );
}
